import { range } from '@mobi/utils'
import type { BettingInformation } from '@classic/Betting-v2/Model/BettingInformation'
import type ObservableSelectedRaceStarters from '@classic/Betting-v2/Model/Observables/ObservableSelectedRaceStarters'
import Guard from '@classic/AppUtils/Framework/Guard'
import type { IObservableStarter } from '@classic/Betting-v2/Model/Observables/IObservableStarter'
import type { CheckBoxSelection } from '../UIElements/CheckboxSelection'
import type { ISelectionResult } from './ISelectionResult'
import type { ISelectionResultProcessor } from './ISelectionResultProcessor'
import MultiLegSelectionResult from './MultiLegSelectionResult'

class CheckBoxProjection {
  constructor(
    public starter: number,
    public selection: CheckBoxSelection
  ) {}
}

class CheckBoxValuesProjection {
  constructor(
    public starter: number,
    public selections: { checked: boolean; value: number }[]
  ) {}
}

export default class MultiLegSelectionResultProcessor implements ISelectionResultProcessor {
  public selections(
    bettingContext: BettingInformation,
    raceNumber: number,
    selections: ObservableSelectedRaceStarters
  ): ISelectionResult {
    Guard.notNull(bettingContext)
    Guard.notNull(selections)

    const starters = selections.getStartersForRace(raceNumber)
    const leg = selections.getLegForRaceNumber(raceNumber)

    if (!starters || starters().length === 0) return new MultiLegSelectionResult(leg, [''])

    if (starters().length === 0 || starters().some(starter => !starter.selection))
      return new MultiLegSelectionResult(leg, [''])

    if (!bettingContext.fieldSelectedAtIndex(leg - 1)) {
      return new MultiLegSelectionResult(leg, [this.dotSeperate(starters)])
    } else if (bettingContext.fieldSelectedAtIndex(leg - 1)) {
      return new MultiLegSelectionResult(leg, ['FD'])
    } else {
      return new MultiLegSelectionResult(leg, [])
    }
  }

  private dotSeperate(stream: ko.ObservableArray<IObservableStarter>): string {
    return stream()
      .map(
        starter =>
          new CheckBoxProjection(starter.number(), starter.selection() as CheckBoxSelection)
      )
      .map(
        checkboxProjection =>
          new CheckBoxValuesProjection(
            checkboxProjection.starter,
            range(checkboxProjection.selection.count()).map(i => ({
              value: i,
              checked: checkboxProjection.selection.selectedAt(i),
            }))
          )
      )
      .filter(checkboxValuesProjection =>
        checkboxValuesProjection.selections.some(
          selection => selection.checked && selection.value === 0
        )
      )
      .map(checkboxValuesProjection => checkboxValuesProjection.starter)
      .sort((a: number, b: number) => a - b)
      .join('.')
  }

  public getSelectionsResult(
    bettingContext: BettingInformation,
    raceNumber: number
  ): ISelectionResult {
    return this.selections(bettingContext, raceNumber, bettingContext.selections)
  }
}
